import { PageLoading } from "@bednbody/ui/components/page-loading";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import type { StaffRole } from "@/lib/jwt-claims";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Omit to allow any authenticated staff role. */
  allow?: StaffRole[];
}

/**
 * UI-layer gate only. This decides what renders — it is not the security
 * boundary. RLS policies (per-table, driven by the `role`/`hub_id` JWT
 * claims) are the actual boundary; a role sneaking past this component would
 * still hit empty results or a Postgres permission error at the DB.
 */
export function ProtectedRoute({ children, allow }: ProtectedRouteProps) {
  const { session, claims, loading, aal } = useAuth();
  const location = useLocation();

  if (loading)
    return (
      <div className="grid min-h-dvh place-items-center">
        <PageLoading label="Loading admin..." />
      </div>
    );

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (aal !== "aal2") {
    return <Navigate to="/login" state={{ from: location, mfaPending: true }} replace />;
  }

  if (!claims) {
    // Signed in, but no valid staff role claim — likely a client account
    // trying to hit /admin, or the access-token hook hasn't run yet.
    return <Navigate to="/login" state={{ from: location, noStaffRole: true }} replace />;
  }

  if (allow && !allow.includes(claims.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
